import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from 'openblock-vm';
import Renderer from 'scratch-render';

import Blocks from '../../containers/blocks.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
// MSTEM 2026-06-18: project-title input relocated here from the menu bar. Rendered
// centered in the second toolbar row (the strip above the blocks workspace, to the
// right of the 250px tab list) for more width + a cleaner modern look. The component
// self-connects to redux (reads state.scratchGui.projectTitle, dispatches setProjectTitle),
// so it needs no extra props beyond an optional className.
import ProjectTitleInput from '../menu-bar/project-title-input.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import Watermark from '../../containers/watermark.jsx';
import Hardware from '../../containers/hardware.jsx';
import HardwareHeader from '../../containers/hardware-header.jsx';

// eslint-disable-next-line no-unused-vars
import Backpack from '../../containers/backpack.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import Cards from '../../containers/cards.jsx';
import Alerts from '../../containers/alerts.jsx';
import DragLayer from '../../containers/drag-layer.jsx';
import ConnectionModal from '../../containers/connection-modal.jsx';
import UploadProgress from '../../containers/upload-progress.jsx';
import TelemetryModal from '../telemetry-modal/telemetry-modal.jsx';
import UpdateModal from '../../containers/update-modal.jsx';
import ThemeManagerModal from '../../containers/theme-manager-modal.jsx';
import RobotManagerModal from '../../containers/robot-manager-modal.jsx';
import KitManagerModal from '../../containers/kit-manager-modal.jsx';
import FirmwareWizardModal from '../../containers/firmware-wizard-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-01 — New project modal mount.
// Inserted alphabetically (NewProject < OpenProject < SaveProject) so
// subsequent Phase 3 plans 03-03/04/05/06 can slot their imports next to
// this one without merge conflicts.
import NewProjectModal from '../../containers/new-project-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-02 — Open project modal mount (alphabetical: N < O).
import OpenProjectModal from '../../containers/open-project-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-03 — Save project modal mount (alphabetical: O < S).
import SaveProjectModal from '../../containers/save-project-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-04 — Share project modal mount (alphabetical: Sa < Sh).
import ShareProjectModal from '../../containers/share-project-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-05 — Shared projects modal mount (alphabetical: Share < Shared).
import SharedProjectsModal from '../../containers/shared-projects-modal.jsx';
// MSTEM-ADDED Phase 3 PRJ-06 — Tutorial modal mount (alphabetical: Shared < Tutorial).
import MstemTutorialModal from '../../containers/mstem-tutorial-modal.jsx';
// MSTEM-ADDED Phase 4 ROB-02..04 — Robot block info ("Thông tin khối Robot") modal mount (DR-05).
import RobotBlockInfoModal from '../../containers/robot-block-info-modal.jsx';
// MSTEM-ADDED Phase 5 SMH-02 — SmartHome dashboard ("Dashboard giám sát thiết bị") modal mount (DR-04).
import SmartHomeDashboardModal from '../../containers/smarthome-dashboard-modal.jsx';
// MSTEM-ADDED Phase 6 EXT-01..04 — "Khối Mở rộng" authoring modal mounts (DR-05).
import DeviceDeclarationModal from '../../containers/device-declaration-modal.jsx';
import BlockBuilderModal from '../../containers/block-builder-modal.jsx';
import LedMatrixDesignerModal from '../../containers/led-matrix-designer-modal.jsx';
import PublishWizardModal from '../../containers/publish-wizard-modal.jsx';
// MSTEM-ADDED UC-43 / UC-47 — "Quản lý ... mở rộng" manager modal mounts.
import ExtDeviceManagerModal from '../../containers/ext-device-manager-modal.jsx';
import ExtBlockManagerModal from '../../containers/ext-block-manager-modal.jsx';
// MSTEM-ADDED Phase 7 AI-01 — AI control panel modal mount (DR-04).
import AiPanelModal from '../../containers/ai-panel-modal.jsx';

import layout, {STAGE_SIZE_MODES} from '../../lib/layout-constants';
import {resolveStageSize} from '../../lib/screen-utils';

import styles from './gui.css';
import addExtensionIcon from './icon--extensions.svg'; // eslint-disable-line no-unused-vars
import codeIcon from './icon--code.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';

// eslint-disable-next-line no-unused-vars
const messages = defineMessages({
    addExtension: {
        id: 'gui.gui.addExtension',
        description: 'Button to add an extension in the target pane',
        defaultMessage: 'Add Extension'
    }
});

// Cache this value to only retrieve it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

const GUIComponent = props => {
    const {
        accountNavOpen,
        activeTabIndex,
        alertsVisible,
        authorId,
        authorThumbnailUrl,
        authorUsername,
        basePath,
        backdropLibraryVisible,
        // eslint-disable-next-line no-unused-vars
        backpackHost,
        // eslint-disable-next-line no-unused-vars
        backpackVisible,
        blocksTabVisible,
        cardsVisible,
        canChangeLanguage,
        canCreateNew,
        canEditTitle,
        canManageFiles,
        canRemix,
        canSave,
        canCreateCopy,
        canShare,
        canUseCloud,
        children,
        connectionModalVisible,
        uploadProgressVisible,
        costumeLibraryVisible,
        costumesTabVisible,
        updateModalVisible,
        themeManagerVisible,
        robotBlockInfoVisible,
        smartHomeDashboardVisible,
        deviceDeclarationVisible,
        blockBuilderVisible,
        ledDesignerVisible,
        publishWizardVisible,
        extDeviceManagerVisible,
        extBlockManagerVisible,
        aiPanelVisible,
        robotManagerVisible,
        kitManagerVisible,
        firmwareWizardVisible,
        newProjectModalVisible,
        openProjectModalVisible,
        saveProjectModalVisible,
        shareProjectModalVisible,
        sharedProjectsModalVisible,
        tutorialModalVisible,
        enableCommunity,
        intl, // eslint-disable-line no-unused-vars
        isCreating,
        isFullScreen,
        isPlayerOnly,
        isRtl,
        isShared,
        isTelemetryEnabled,
        loading,
        logo,
        renderLogin,
        onClickAbout,
        onClickAccountNav,
        onCloseAccountNav,
        onLogOut,
        onOpenRegistration,
        onToggleLoginOpen,
        onAbortUpdate,
        onActivateCostumesTab,
        onActivateSoundsTab,
        onActivateTab,
        onClickLogo,
        onClickCheckUpdate,
        onClickUpdate,
        onClickClearCache,
        onClickInstallDriver,
        onExtensionButtonClick, // eslint-disable-line no-unused-vars
        onProjectTelemetryEvent,
        onRequestCloseBackdropLibrary,
        onRequestCloseCostumeLibrary,
        onRequestCloseTelemetryModal,
        onSeeCommunity,
        onShare,
        onShowPrivacyPolicy,
        onStartSelectingFileUpload,
        onShowMessageBox,
        onTelemetryModalCancel,
        onTelemetryModalOptIn,
        onTelemetryModalOptOut,
        showComingSoon,
        soundsTabVisible,
        stageSizeMode,
        targetIsStage,
        telemetryModalVisible,
        tipsLibraryVisible,
        vm,
        isRealtimeMode,
        ...componentProps
    } = omit(props, 'dispatch');
    if (children) {
        return <Box {...componentProps}>{children}</Box>;
    }

    const tabClassNames = {
        tabs: styles.tabs,
        tab: classNames(tabStyles.reactTabsTab, styles.tab),
        tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
        tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
        tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
        tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
    };

    if (isRendererSupported === null) {
        isRendererSupported = Renderer.isSupported();
    }

    return (<MediaQuery minWidth={layout.fullSizeMinWidth}>{isFullSize => {
        const stageSize = resolveStageSize(stageSizeMode, isFullSize);

        return isPlayerOnly ? (
            <StageWrapper
                isFullScreen={isFullScreen}
                isRendererSupported={isRendererSupported}
                isRtl={isRtl}
                loading={loading}
                stageSize={STAGE_SIZE_MODES.large}
                vm={vm}
            >
                {alertsVisible ? (
                    <Alerts
                        vm={vm}
                        className={styles.alertsContainer}
                    />
                ) : null}
            </StageWrapper>
        ) : (
            <Box
                className={styles.pageWrapper}
                dir={isRtl ? 'rtl' : 'ltr'}
                {...componentProps}
            >
                {telemetryModalVisible ? (
                    <TelemetryModal
                        isRtl={isRtl}
                        isTelemetryEnabled={isTelemetryEnabled}
                        onCancel={onTelemetryModalCancel}
                        onOptIn={onTelemetryModalOptIn}
                        onOptOut={onTelemetryModalOptOut}
                        onRequestClose={onRequestCloseTelemetryModal}
                        onShowPrivacyPolicy={onShowPrivacyPolicy}
                    />
                ) : null}
                {loading ? (
                    <Loader />
                ) : null}
                {isCreating ? (
                    <Loader messageId="gui.loader.creating" />
                ) : null}
                {isRendererSupported ? null : (
                    <WebGlModal isRtl={isRtl} />
                )}
                {tipsLibraryVisible ? (
                    <TipsLibrary />
                ) : null}
                {cardsVisible ? (
                    <Cards />
                ) : null}
                {alertsVisible ? (
                    <Alerts
                        vm={vm}
                        className={styles.alertsContainer}
                    />
                ) : null}
                {connectionModalVisible ? (
                    <ConnectionModal
                        vm={vm}
                    />
                ) : null}
                {uploadProgressVisible ? (
                    <UploadProgress
                        vm={vm}
                    />
                ) : null}
                {costumeLibraryVisible ? (
                    <CostumeLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseCostumeLibrary}
                    />
                ) : null}
                {backdropLibraryVisible ? (
                    <BackdropLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseBackdropLibrary}
                    />
                ) : null}
                {updateModalVisible ? (
                    <UpdateModal
                        vm={vm}
                        onAbortUpdate={onAbortUpdate}
                        onClickUpdate={onClickUpdate}
                        onShowMessageBox={onShowMessageBox}
                    />
                ) : null}
                {/* MSTEM-ADDED Phase 2 GEN-02 — Theme manager modal mount */}
                {themeManagerVisible ? (
                    <ThemeManagerModal />
                ) : null}
                {/* MSTEM-ADDED Phase 2 GEN-03 — Robot manager modal mount */}
                {robotManagerVisible ? (
                    <RobotManagerModal />
                ) : null}
                {/* MSTEM-ADDED Phase 2 GEN-04 — Kit manager modal mount */}
                {kitManagerVisible ? (
                    <KitManagerModal />
                ) : null}
                {/* MSTEM-ADDED Phase 2 GEN-05 — Firmware OTA wizard modal mount */}
                {firmwareWizardVisible ? (
                    <FirmwareWizardModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-01 — New project modal mount (alphabetical: NewProject) */}
                {newProjectModalVisible ? (
                    <NewProjectModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-02 — Open project modal mount (alphabetical: N < O) */}
                {openProjectModalVisible ? (
                    <OpenProjectModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-03 — Save project modal mount (alphabetical: O < S) */}
                {saveProjectModalVisible ? (
                    <SaveProjectModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-04 — Share project modal mount (alphabetical: Sa < Sh) */}
                {shareProjectModalVisible ? (
                    <ShareProjectModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-05 — Shared projects modal mount (alphabetical: Share < Shared) */}
                {sharedProjectsModalVisible ? (
                    <SharedProjectsModal />
                ) : null}
                {/* MSTEM-ADDED Phase 3 PRJ-06 — Tutorial modal mount (alphabetical: Shared < Tutorial) */}
                {tutorialModalVisible ? (
                    <MstemTutorialModal />
                ) : null}
                {/* MSTEM-ADDED Phase 4 ROB-02..04 — Robot block info modal mount (DR-05) */}
                {robotBlockInfoVisible ? (
                    <RobotBlockInfoModal />
                ) : null}
                {/* MSTEM-ADDED Phase 5 SMH-02 — SmartHome dashboard modal mount (DR-04) */}
                {smartHomeDashboardVisible ? (
                    <SmartHomeDashboardModal />
                ) : null}
                {/* MSTEM-ADDED Phase 6 EXT-01 — Device declaration wizard modal mount (DR-05) */}
                {deviceDeclarationVisible ? (
                    <DeviceDeclarationModal />
                ) : null}
                {/* MSTEM-ADDED Phase 6 EXT-02 — Block builder modal mount (DR-05) */}
                {blockBuilderVisible ? (
                    <BlockBuilderModal />
                ) : null}
                {/* MSTEM-ADDED Phase 6 EXT-03 — LED 8×8 designer modal mount (DR-05) */}
                {ledDesignerVisible ? (
                    <LedMatrixDesignerModal />
                ) : null}
                {/* MSTEM-ADDED Phase 6 EXT-04 — Publish wizard modal mount (DR-05) */}
                {publishWizardVisible ? (
                    <PublishWizardModal />
                ) : null}
                {/* MSTEM-ADDED UC-43 — Extension device manager modal mount */}
                {extDeviceManagerVisible ? (
                    <ExtDeviceManagerModal />
                ) : null}
                {/* MSTEM-ADDED UC-47 — Extension block manager modal mount */}
                {extBlockManagerVisible ? (
                    <ExtBlockManagerModal />
                ) : null}
                {/* MSTEM-ADDED Phase 7 AI-01 — AI panel modal mount */}
                {aiPanelVisible ? (
                    <AiPanelModal />
                ) : null}
                <MenuBar
                    accountNavOpen={accountNavOpen}
                    authorId={authorId}
                    authorThumbnailUrl={authorThumbnailUrl}
                    authorUsername={authorUsername}
                    canChangeLanguage={canChangeLanguage}
                    canCreateCopy={canCreateCopy}
                    canCreateNew={canCreateNew}
                    canEditTitle={canEditTitle}
                    canManageFiles={canManageFiles}
                    canRemix={canRemix}
                    canSave={canSave}
                    canShare={canShare}
                    className={styles.menuBarPosition}
                    enableCommunity={enableCommunity}
                    isShared={isShared}
                    logo={logo}
                    renderLogin={renderLogin}
                    showComingSoon={showComingSoon}
                    onClickAbout={onClickAbout}
                    onClickAccountNav={onClickAccountNav}
                    onClickLogo={onClickLogo}
                    onCloseAccountNav={onCloseAccountNav}
                    onLogOut={onLogOut}
                    onOpenRegistration={onOpenRegistration}
                    onProjectTelemetryEvent={onProjectTelemetryEvent}
                    onSeeCommunity={onSeeCommunity}
                    onShare={onShare}
                    onStartSelectingFileUpload={onStartSelectingFileUpload}
                    onShowMessageBox={onShowMessageBox}
                    onToggleLoginOpen={onToggleLoginOpen}
                    onClickCheckUpdate={onClickCheckUpdate}
                    onClickClearCache={onClickClearCache}
                    onClickInstallDriver={onClickInstallDriver}
                />
                <Box className={styles.bodyWrapper}>
                    <Box className={styles.flexWrapper}>
                        <Box className={styles.editorWrapper}>
                            {/* MSTEM 2026-06-18: project title, relocated from the menu bar.
                                Absolutely positioned in the second-row strip, occupying the
                                space to the right of the 250px tab list and horizontally
                                centering its input pill above the blocks workspace. */}
                            {canEditTitle ? (
                                <div className={styles.projectTitleStrip}>
                                    <ProjectTitleInput
                                        className={styles.projectTitleField}
                                    />
                                </div>
                            ) : null}
                            <Tabs
                                forceRenderTabPanel
                                className={tabClassNames.tabs}
                                selectedIndex={activeTabIndex}
                                selectedTabClassName={tabClassNames.tabSelected}
                                selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                onSelect={onActivateTab}
                            >
                                <TabList className={tabClassNames.tabList}>
                                    <Tab className={tabClassNames.tab}>
                                        <img
                                            draggable={false}
                                            src={codeIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Code"
                                            description="Button to get to the code panel"
                                            id="gui.gui.codeTab"
                                        />
                                    </Tab>
                                    <Tab
                                        className={classNames(tabClassNames.tab,
                                            isRealtimeMode ? styles.hideCustomAndSoundTab :
                                                styles.showCustomAndSoundTab)}
                                        onClick={onActivateCostumesTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={costumesIcon}
                                        />
                                        {targetIsStage ? (
                                            <FormattedMessage
                                                defaultMessage="Backdrops"
                                                description="Button to get to the backdrops panel"
                                                id="gui.gui.backdropsTab"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Costumes"
                                                description="Button to get to the costumes panel"
                                                id="gui.gui.costumesTab"
                                            />
                                        )}
                                    </Tab>
                                    <Tab
                                        className={classNames(tabClassNames.tab,
                                            isRealtimeMode ? styles.hideCustomAndSoundTab :
                                                styles.showCustomAndSoundTab)}
                                        onClick={onActivateSoundsTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={soundsIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Sounds"
                                            description="Button to get to the sounds panel"
                                            id="gui.gui.soundsTab"
                                        />
                                    </Tab>
                                </TabList>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    <Box className={styles.blocksWrapper}>
                                        <Blocks
                                            canUseCloud={canUseCloud}
                                            grow={1}
                                            isVisible={blocksTabVisible}
                                            options={{
                                                media: `${basePath}static/blocks-media/`
                                            }}
                                            stageSize={stageSize}
                                            vm={vm}
                                            onShowMessageBox={onShowMessageBox}
                                        />
                                    </Box>
                                    {/* BRANDING 2026-06-17: native Scratch extension library (🧩 pen/music/
                                        micro:bit/LEGO — non-Mobifone hardware, English) hidden. The mSTEM
                                        "Khối Mở rộng" requirement is met by the custom "Mở rộng" menu
                                        (declare device/block/LED/publish). Avoids A/B duplication + the
                                        "blocks without matching hardware" mismatch. Restore by un-commenting.
                                    <Box className={styles.extensionButtonContainer}>
                                        <button
                                            className={styles.extensionButton}
                                            title={intl.formatMessage(messages.addExtension)}
                                            onClick={onExtensionButtonClick}
                                        >
                                            <img
                                                className={styles.extensionButtonIcon}
                                                draggable={false}
                                                src={addExtensionIcon}
                                            />
                                        </button>
                                    </Box>
                                    */}
                                    <Box className={styles.watermark}>
                                        <Watermark />
                                    </Box>
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {costumesTabVisible ? <CostumeTab vm={vm} /> : null}
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {soundsTabVisible ? <SoundTab
                                        vm={vm}
                                        onShowMessageBox={onShowMessageBox}
                                    /> : null}
                                </TabPanel>
                            </Tabs>
                            {/*
                                    backpackVisible ? (
                                        <Backpack host={backpackHost} />
                                    ) : null
                                */}
                        </Box>
                        <Box
                            className={classNames(styles.stageAndTargetWrapper, styles[stageSize],
                                isRealtimeMode ? styles.showStage : styles.hideStage)}
                        >
                            <StageWrapper
                                isFullScreen={isFullScreen}
                                isRendererSupported={isRendererSupported}
                                isRtl={isRtl}
                                stageSize={stageSize}
                                vm={vm}
                            />
                            <Box className={styles.targetWrapper}>
                                <TargetPane
                                    stageSize={stageSize}
                                    vm={vm}
                                />
                            </Box>
                        </Box>
                        {((isRealtimeMode === false) && (stageSizeMode !== STAGE_SIZE_MODES.hide)) ? (
                            <Hardware
                                vm={vm}
                                stageSize={stageSize}
                            />) : null
                        }
                    </Box>
                    <DragLayer />
                    {(isRealtimeMode === false) ? (
                        <HardwareHeader
                            vm={vm}
                            stageSize={stageSize}
                        />) : null
                    }
                </Box>
            </Box>
        );
    }}</MediaQuery>);
};

GUIComponent.propTypes = {
    accountNavOpen: PropTypes.bool,
    activeTabIndex: PropTypes.number,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    backdropLibraryVisible: PropTypes.bool,
    backpackHost: PropTypes.string,
    backpackVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    canChangeLanguage: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    loading: PropTypes.bool,
    logo: PropTypes.string,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onClickAccountNav: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickCheckUpdate: PropTypes.func,
    onAbortUpdate: PropTypes.func,
    onClickUpdate: PropTypes.func,
    onClickClearCache: PropTypes.func,
    onClickInstallDriver: PropTypes.func,
    onCloseAccountNav: PropTypes.func,
    onExtensionButtonClick: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onRequestCloseTelemetryModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onShowPrivacyPolicy: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onShowMessageBox: PropTypes.func.isRequired,
    onTabSelect: PropTypes.func,
    onTelemetryModalCancel: PropTypes.func,
    onTelemetryModalOptIn: PropTypes.func,
    onTelemetryModalOptOut: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    renderLogin: PropTypes.func,
    showComingSoon: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    targetIsStage: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    themeManagerVisible: PropTypes.bool,
    robotBlockInfoVisible: PropTypes.bool,
    smartHomeDashboardVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 6 EXT-01..04 — "Khối Mở rộng" authoring modal flags.
    deviceDeclarationVisible: PropTypes.bool,
    blockBuilderVisible: PropTypes.bool,
    ledDesignerVisible: PropTypes.bool,
    publishWizardVisible: PropTypes.bool,
    extDeviceManagerVisible: PropTypes.bool,
    extBlockManagerVisible: PropTypes.bool,
    // MSTEM-ADDED Phase 7 AI-01 — AI control panel modal flag.
    aiPanelVisible: PropTypes.bool,
    robotManagerVisible: PropTypes.bool,
    kitManagerVisible: PropTypes.bool,
    firmwareWizardVisible: PropTypes.bool,
    newProjectModalVisible: PropTypes.bool,
    openProjectModalVisible: PropTypes.bool,
    saveProjectModalVisible: PropTypes.bool,
    shareProjectModalVisible: PropTypes.bool,
    sharedProjectsModalVisible: PropTypes.bool,
    tutorialModalVisible: PropTypes.bool,
    tipsLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRealtimeMode: PropTypes.bool
};
GUIComponent.defaultProps = {
    backpackHost: null,
    backpackVisible: false,
    basePath: './',
    canChangeLanguage: true,
    canCreateNew: false,
    canEditTitle: false,
    canManageFiles: true,
    canRemix: false,
    canSave: false,
    canCreateCopy: false,
    canShare: false,
    canUseCloud: false,
    enableCommunity: false,
    isCreating: false,
    isShared: false,
    loading: false,
    showComingSoon: false,
    stageSizeMode: STAGE_SIZE_MODES.large
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    stageSizeMode: state.scratchGui.stageSize.stageSize
});

export default injectIntl(connect(
    mapStateToProps
)(GUIComponent));
